import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// Define the shape of the data our context will provide
interface GroupContextState {
  groupId: string | null;
  isCoopMode: boolean;
  joinGroup: (id: string) => void;
  leaveGroup: () => void;
}

// Create the context with a default value
const GroupContext = createContext<GroupContextState>({
  groupId: null,
  isCoopMode: false,
  joinGroup: () => {},
  leaveGroup: () => {},
});

// Create the Provider component. This is where all the logic now lives.
export const GroupProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [groupId, setGroupId] = useState<string | null>(
    localStorage.getItem("currentGroupId"),
  );

  useEffect(() => {
    // This logic is identical to the old hook, but now it only runs ONCE for the entire app.
    const params = new URLSearchParams(window.location.search);
    const groupIdFromQuery = params.get("joinGroup");

    if (groupIdFromQuery) {
      setGroupId(groupIdFromQuery);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  useEffect(() => {
    if (groupId) {
      localStorage.setItem("currentGroupId", groupId);
    } else {
      localStorage.removeItem("currentGroupId");
    }
  }, [groupId]);

  const joinGroup = useCallback((id: string) => {
    setGroupId(id);
  }, []);

  const leaveGroup = useCallback(() => {
    setGroupId(null);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders of consumers.
  // This is a critical performance optimization.
  const value = useMemo(
    () => ({
      groupId,
      isCoopMode: !!groupId,
      joinGroup,
      leaveGroup,
    }),
    [groupId, joinGroup, leaveGroup],
  );

  return (
    <GroupContext.Provider value={value}>{children}</GroupContext.Provider>
  );
};

// Create the custom hook. It's now just a simple one-liner!
export const useGroup = (): GroupContextState => {
  return useContext(GroupContext);
};
